import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ContactMultiPicker } from "../../components/ContactMultiPicker";
import { Divider } from "../../components/Divider";
import { Pagination } from "../../components/Pagination";
import { StatusLine } from "../../components/StatusLine";
import {
  useAuthoringRuns,
  useDeleteAuthoringRun,
  useStartAuthoringRun,
} from "../../hooks/useAuthoring";
import type { Contact, Icp, IcpSampleContact } from "../../lib/types";
import {
  DEFAULT_MAX_ITERATIONS,
  STATUS_LABELS,
  fileToText,
  formatTime,
} from "./shared";

const RUNS_PAGE_SIZE = 10;

export function RunStart({
  icp,
  storedSamples,
}: {
  icp: Icp;
  storedSamples: IcpSampleContact[];
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const start = useStartAuthoringRun();
  const [runsPage, setRunsPage] = useState(1);
  const { data: runsPageData, isLoading: runsLoading } = useAuthoringRuns(
    icp.name,
    { page: runsPage, pageSize: RUNS_PAGE_SIZE },
  );
  const runs = runsPageData?.runs;
  const runsTotal = runsPageData?.total ?? 0;
  const runsTotalPages = Math.max(1, Math.ceil(runsTotal / RUNS_PAGE_SIZE));
  const deleteRun = useDeleteAuthoringRun();

  const storedPositiveCount = storedSamples.filter(
    (sample) => sample.label === "positive",
  ).length;
  const storedNegativeCount = storedSamples.filter(
    (sample) => sample.label === "negative",
  ).length;
  const hasAnyStored = storedPositiveCount > 0 || storedNegativeCount > 0;

  const [mode, setMode] = useState<"improve" | "validate">("improve");
  const [useStoredSamples, setUseStoredSamples] = useState(hasAnyStored);
  const [maxIterations, setMaxIterations] = useState(
    String(DEFAULT_MAX_ITERATIONS),
  );
  const [positiveCsv, setPositiveCsv] = useState<string>("");
  const [negativeCsv, setNegativeCsv] = useState<string>("");
  const [positiveContacts, setPositiveContacts] = useState<Contact[]>([]);
  const [negativeContacts, setNegativeContacts] = useState<Contact[]>([]);
  const [positiveFilename, setPositiveFilename] = useState<string>("");
  const [negativeFilename, setNegativeFilename] = useState<string>("");
  const [runInstructions, setRunInstructions] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const positiveInputRef = useRef<HTMLInputElement>(null);
  const negativeInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (
    kind: "positive" | "negative",
    file: File | undefined,
  ) => {
    if (!file) return;
    try {
      const text = await fileToText(file);
      if (kind === "positive") {
        setPositiveCsv(text);
        setPositiveFilename(file.name);
      } else {
        setNegativeCsv(text);
        setNegativeFilename(file.name);
      }
    } catch (err) {
      setError(`Could not read CSV: ${err instanceof Error ? err.message : err}`);
    }
  };

  const clearFile = (kind: "positive" | "negative") => {
    if (kind === "positive") {
      setPositiveCsv("");
      setPositiveFilename("");
      if (positiveInputRef.current) positiveInputRef.current.value = "";
    } else {
      setNegativeCsv("");
      setNegativeFilename("");
      if (negativeInputRef.current) negativeInputRef.current.value = "";
    }
  };

  const togglePick = (
    kind: "positive" | "negative",
    contact: Contact,
  ) => {
    const setter =
      kind === "positive" ? setPositiveContacts : setNegativeContacts;
    setter((prev) =>
      prev.some((existing) => existing.id === contact.id)
        ? prev.filter((existing) => existing.id !== contact.id)
        : [...prev, contact],
    );
  };

  const removePick = (kind: "positive" | "negative", contactId: string) => {
    const setter =
      kind === "positive" ? setPositiveContacts : setNegativeContacts;
    setter((prev) => prev.filter((existing) => existing.id !== contactId));
  };

  const onStart = async () => {
    setError(null);
    const max = Number(maxIterations);
    if (mode === "improve" && (!Number.isInteger(max) || max < 1 || max > 10)) {
      setError("Max iterations must be an integer between 1 and 10.");
      return;
    }

    const positiveIdList = positiveContacts.map((contact) => contact.id);
    const negativeIdList = negativeContacts.map((contact) => contact.id);

    const adhocPositive = Boolean(positiveCsv) || positiveIdList.length > 0;
    const adhocNegative = Boolean(negativeCsv) || negativeIdList.length > 0;

    if (!useStoredSamples) {
      if (!adhocPositive && !adhocNegative) {
        setError(
          "With \"use stored samples\" off, provide at least one positive or negative sample (CSV or contacts).",
        );
        return;
      }
    } else if (!hasAnyStored && !adhocPositive && !adhocNegative) {
      setError(
        "This ICP has no stored samples yet — add some on the ICP page or include CSV / contacts here.",
      );
      return;
    }

    try {
      const run = await start.mutateAsync({
        icp_name: icp.name,
        mode,
        use_stored_samples: useStoredSamples,
        max_iterations: mode === "validate" ? 1 : max,
        positive_csv: positiveCsv || null,
        negative_csv: negativeCsv || null,
        positive_processed_contact_ids: positiveIdList,
        negative_processed_contact_ids: negativeIdList,
        run_instructions:
          mode === "improve" && runInstructions.trim()
            ? runInstructions.trim()
            : null,
      });
      enqueueSnackbar(`Authoring run started.`, { variant: "success" });
      navigate(`/icps/${icp.name}/improve/${run.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="card">
      <h2>
        {mode === "validate" ? "Validate prompt" : "Improve prompt"} —{" "}
        {icp.display_name}
      </h2>
      <p className="breadcrumb">
        <Link to={`/icps/${icp.name}`}>← Back to ICP</Link>
      </p>
      <p className="card-desc">
        {mode === "validate"
          ? `Run the current live prompt against the sample set once. No rewrite, no new prompt version — useful for re-checking after a manual edit or a model change.`
          : `Iteratively rewrite the system prompt until every positive scores ≥ ${icp.min_score} and every negative scores below it.`}
      </p>
      {mode === "improve" && !icp.prompt && (
        <p className="card-desc">
          <strong>Heads up:</strong> this ICP has no prompt yet — the first
          iteration will draft one from the ICP context and samples.
        </p>
      )}

      <div className="field-row">
        <div className="field">
          <label htmlFor="run-mode">Mode</label>
          <select
            id="run-mode"
            value={mode}
            onChange={(event) =>
              setMode(event.target.value as "improve" | "validate")
            }
          >
            <option value="improve">Improve (rewrite loop)</option>
            <option value="validate" disabled={!icp.prompt}>
              Validate only (score live prompt)
              {!icp.prompt ? ": needs a live prompt first" : ""}
            </option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="max-iter">Max iterations (1–10)</label>
          <input
            id="max-iter"
            type="number"
            min={1}
            max={10}
            value={maxIterations}
            onChange={(event) => setMaxIterations(event.target.value)}
            disabled={mode === "validate"}
          />
          {mode === "validate" && (
            <p className="card-desc">Forced to 1 in validate mode.</p>
          )}
        </div>
      </div>

      {mode === "improve" && (
        <div className="field" style={{ marginTop: 8 }}>
          <label htmlFor="run-instructions">
            Run-specific instructions{" "}
            <span className="card-desc" style={{ fontWeight: "normal" }}>
              — applied on top of the ICP context for this run only
            </span>
          </label>
          <textarea
            id="run-instructions"
            rows={3}
            value={runInstructions}
            onChange={(event) => setRunInstructions(event.target.value)}
            placeholder="e.g. Remove the dach_expansion_fit axis. Tighten the industry rules to exclude e-commerce."
          />
        </div>
      )}

      <div className="checkbox-field">
        <label>
          <input
            type="checkbox"
            checked={useStoredSamples}
            onChange={(event) => setUseStoredSamples(event.target.checked)}
            disabled={!hasAnyStored}
          />{" "}
          Use stored sample set{" "}
          <span className="card-desc">
            ({storedPositiveCount} positive · {storedNegativeCount} negative
            {hasAnyStored ? "" : " — none yet, add some on the ICP page"})
          </span>
        </label>
      </div>

      <h3 style={{ marginTop: 16 }}>
        Ad-hoc samples (not persisted)
        <span className="card-desc" style={{ fontWeight: "normal" }}>
          {" "}
          — merged with the stored set above; for samples you want to keep,
          add them on the ICP page.
        </span>
      </h3>

      <div className="field-row">
        <div className="field">
          <label htmlFor="positive-csv">Positive examples (CSV)</label>
          <input
            id="positive-csv"
            ref={positiveInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(event) =>
              handleFile("positive", event.target.files?.[0])
            }
          />
          {positiveFilename && (
            <p className="card-desc">
              Loaded <strong>{positiveFilename}</strong> ({positiveCsv.length}{" "}
              chars).{" "}
              <button
                type="button"
                className="button-link"
                onClick={() => clearFile("positive")}
              >
                Clear
              </button>
            </p>
          )}
          <label style={{ marginTop: 8 }}>…or pick from processed contacts</label>
          <ContactMultiPicker
            selected={positiveContacts}
            onToggle={(contact) => togglePick("positive", contact)}
            onRemove={(contactId) => removePick("positive", contactId)}
            accentColor="var(--success)"
          />
        </div>

        <div className="field">
          <label htmlFor="negative-csv">Negative examples (CSV)</label>
          <input
            id="negative-csv"
            ref={negativeInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(event) =>
              handleFile("negative", event.target.files?.[0])
            }
          />
          {negativeFilename && (
            <p className="card-desc">
              Loaded <strong>{negativeFilename}</strong> ({negativeCsv.length}{" "}
              chars).{" "}
              <button
                type="button"
                className="button-link"
                onClick={() => clearFile("negative")}
              >
                Clear
              </button>
            </p>
          )}
          <label style={{ marginTop: 8 }}>…or pick from processed contacts</label>
          <ContactMultiPicker
            selected={negativeContacts}
            onToggle={(contact) => togglePick("negative", contact)}
            onRemove={(contactId) => removePick("negative", contactId)}
            accentColor="var(--error)"
          />
        </div>
      </div>

      <button onClick={onStart} disabled={start.isPending}>
        {start.isPending
          ? "Starting…"
          : mode === "validate"
          ? "Start validate run"
          : "Start authoring run"}
      </button>
      {error && <StatusLine variant="error" message={error} />}

      <Divider />
      <h3 style={{ marginTop: 24 }}>
        Previous runs{runsTotal > 0 ? ` (${runsTotal})` : ""}
      </h3>
      {runsLoading ? (
        <StatusLine variant="running" message="Loading previous runs…" />
      ) : !runs || runs.length === 0 ? (
        <p className="card-desc">No previous runs for this ICP yet.</p>
      ) : (
        <>
          <div className="result">
            {runs.map((run) => (
              <div
                key={run.id}
                className="contact"
                style={{ display: "flex", gap: 12, alignItems: "stretch" }}
              >
                <Link
                  to={`/icps/${icp.name}/improve/${run.id}`}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <div className="contact-top">
                    <div>
                      <div className="contact-name">
                        {STATUS_LABELS[run.status]}
                      </div>
                      <code className="contact-company">{run.id}</code>
                    </div>
                    <span>{formatTime(run.created_at)}</span>
                  </div>
                  <div className="chip-row">
                    <span className="chip">
                      Iterations {run.current_iteration}/{run.max_iterations}
                    </span>
                    <span className="chip">
                      Samples {run.sample_contacts_count ?? "?"}
                    </span>
                  </div>
                </Link>
                <button
                  type="button"
                  className="button-link"
                  style={{ color: "var(--error)", alignSelf: "flex-start" }}
                  disabled={deleteRun.isPending}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        `Delete run ${run.id}? This cannot be undone. Any prompt version promoted from this run is kept.`,
                      )
                    )
                      return;
                    try {
                      await deleteRun.mutateAsync({
                        run_id: run.id,
                        icp_name: icp.name,
                      });
                      enqueueSnackbar("Run deleted.", { variant: "success" });
                      if (runs.length === 1 && runsPage > 1) {
                        setRunsPage((current) => current - 1);
                      }
                    } catch (err) {
                      enqueueSnackbar(
                        `Delete failed: ${
                          err instanceof Error ? err.message : err
                        }`,
                        { variant: "error" },
                      );
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
          <Pagination
            page={runsPage}
            totalPages={runsTotalPages}
            onChange={setRunsPage}
          />
        </>
      )}
    </section>
  );
}
